<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen py-10 px-4">
    <AppHeader />

    <div class="max-w-2xl mx-auto">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold">Pagamento do Serviço</h1>
          <p class="text-gray-500 text-sm mt-1">Finalize o pagamento para concluir o agendamento.</p>
        </div>
        <router-link :to="{ name: 'appointment-details', params: { id: route.params.id } }" class="px-4 py-2 rounded-xl bg-white border shadow-sm hover:bg-gray-50 transition text-sm">
          Voltar
        </router-link>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex justify-center py-20">
        <div class="w-10 h-10 border-4 border-[#b99a52] border-t-transparent rounded-full animate-spin"></div>
      </div>

      <!-- Erro -->
      <div v-else-if="errorMsg" class="bg-white rounded-2xl border border-red-200 p-6 shadow text-center">
        <p class="text-red-600 font-semibold mb-2">Não foi possível carregar.</p>
        <p class="text-gray-500 text-sm mb-4">{{ errorMsg }}</p>
        <button @click="loadAppointment" class="px-6 py-2 bg-[#b99a52] text-white rounded-xl font-semibold">Tentar novamente</button>
      </div>

      <template v-else-if="appointment">
        <!-- Pago -->
        <div v-if="isPaid" class="bg-white rounded-2xl border border-green-200 p-6 shadow text-center">
          <div class="text-5xl mb-3"></div>
          <h2 class="text-xl font-bold text-green-700">Pagamento confirmado!</h2>
          <p class="text-gray-500 text-sm mt-2 mb-4">Agradecemos pelo pagamento. Não esqueça de avaliar o prestador!</p>
          <router-link :to="{ name: 'appointment-details', params: { id: appointment.id } }" class="block py-3 bg-[#b99a52] text-white rounded-xl font-semibold">
            Ver Agendamento
          </router-link>
        </div>

        <!-- Aguardando pagamento -->
        <template v-else>
          <!-- Resumo de preços -->
          <div class="bg-white rounded-2xl border border-[#e0dcd6] shadow p-5 mb-5">
            <h2 class="font-semibold text-[#152a34] mb-4">Resumo do Pagamento</h2>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="text-gray-500">Serviço</span>
                <span>{{ appointment.service?.name }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Valor do serviço</span>
                <span>R$ {{ formatPrice(servicePrice) }}</span>
              </div>
              <!--<div class="flex justify-between">
                <span class="text-gray-500">Taxa da plataforma</span>
                <span>R$ {{ formatPrice(platformFee) }}</span>
              </div>-->
              <div class="flex justify-between font-bold text-[#152a34] border-t pt-2 mt-2">
                <span>Total</span>
                <span class="text-lg">R$ {{ formatPrice(servicePrice) }}</span>
              </div>
            </div>
          </div>

          <!-- Opções de pagamento -->
          <div class="grid grid-cols-2 gap-3 mb-5">
            <button
              @click="selectedMethod = 'pix'"
              :class="['p-4 rounded-2xl border-2 transition flex flex-col items-center gap-2', selectedMethod === 'pix' ? 'border-[#b99a52] bg-[#fdf8f0]' : 'border-gray-200 bg-white hover:border-gray-300']"
            >
              <span class="text-2xl">PIX</span>
              <span class="text-xs font-medium text-[#152a34]">Pagamento instantâneo</span>
            </button>
            <button
              @click="selectedMethod = 'card'"
              :class="['p-4 rounded-2xl border-2 transition flex flex-col items-center gap-2', selectedMethod === 'card' ? 'border-[#b99a52] bg-[#fdf8f0]' : 'border-gray-200 bg-white hover:border-gray-300']"
            >
              <span class="text-2xl">💳</span>
              <span class="text-xs font-medium text-[#152a34]">Cartão de crédito</span>
            </button>
          </div>

          <!-- PIX: QR Code -->
          <div v-if="selectedMethod === 'pix' && pixData" class="bg-white rounded-2xl border border-[#e0dcd6] shadow p-5 mb-5">
            <h3 class="font-semibold text-[#152a34] mb-4 text-center">Escaneie o QR Code</h3>
            <div class="flex justify-center mb-4">
              <img v-if="pixData.imagem_qrcode" :src="pixData.imagem_qrcode" alt="QR Code PIX" class="w-48 h-48 rounded-lg" />
            </div>
            <div class="bg-gray-50 rounded-xl p-3 text-xs text-gray-600 break-all mb-3">{{ pixData.qr_code }}</div>
            <button
              @click="copyPix"
              class="w-full py-2.5 bg-[#152a34] text-white rounded-xl text-sm font-semibold hover:bg-[#1e3a4a] transition flex items-center justify-center gap-2"
            >
              <span class="mdi" :class="copied ? 'mdi-check' : 'mdi-content-copy'"></span>
              {{ copied ? 'Copiado!' : 'Copiar código PIX' }}
            </button>
            <p class="text-center text-xs text-gray-400 mt-3 flex items-center justify-center gap-1">
              <span v-if="pollingActive" class="mdi mdi-loading animate-spin"></span>
              Aguardando confirmação do pagamento...
            </p>
          </div>

          <!-- Botão pagar -->
          <button
            v-if="!pixData || selectedMethod === 'card'"
            @click="pay"
            :disabled="payLoading"
            class="w-full py-4 bg-[#b99a52] text-white rounded-xl font-bold text-base hover:bg-[#a08840] transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <span v-if="payLoading" class="mdi mdi-loading animate-spin"></span>
            {{ payLoading ? 'Processando...' : (selectedMethod === 'pix' ? 'Gerar QR Code PIX' : 'Pagar com Cartão') }}
          </button>
        </template>
      </template>
    </div>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getAppointment, createAppointmentPix, createAppointmentCheckout, getAppointmentPaymentStatus } from '@/services/api'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'

const route = useRoute()
const router = useRouter()

const appointment = ref(null)
const loading = ref(true)
const errorMsg = ref('')

const selectedMethod = ref('pix')
const pixData = ref(null)
const payLoading = ref(false)
const copied = ref(false)
const isPaid = ref(false)

let pollingInterval = null
const pollingActive = ref(false)

const FEE_PCT = 0.07

const servicePrice = computed(() => Number(appointment.value?.service?.price_with_fee || 0))
const platformFee = computed(() => parseFloat((servicePrice.value * FEE_PCT).toFixed(2)))
const totalAmount = computed(() => parseFloat((servicePrice.value + platformFee.value).toFixed(2)))

function formatPrice (p) {
  return Number(p || 0).toFixed(2).replace('.', ',')
}

async function loadAppointment () {
  errorMsg.value = ''
  loading.value = true
  try {
    const { data } = await getAppointment(route.params.id)
    appointment.value = data.data
    if (['completed'].includes(appointment.value.status) && appointment.value.payment_id) {
      isPaid.value = true
    }
  } catch (err) {
    errorMsg.value = err?.response?.data?.error || 'Erro ao carregar agendamento.'
  } finally {
    loading.value = false
  }
}

async function pay () {
  payLoading.value = true
  try {
    if (selectedMethod.value === 'pix') {
      const { data } = await createAppointmentPix(appointment.value.id)
      pixData.value = data.data?.payment
      startPolling()
    } else {
      const { data } = await createAppointmentCheckout(appointment.value.id)
      const url = data.data?.checkout?.checkout_url
      if (url) window.location.href = url
    }
  } catch (err) {
    errorMsg.value = err?.response?.data?.error || 'Erro ao processar pagamento.'
  } finally {
    payLoading.value = false
  }
}

function copyPix () {
  if (!pixData.value?.qr_code) return
  navigator.clipboard.writeText(pixData.value.qr_code).then(() => {
    copied.value = true
    setTimeout(() => { copied.value = false }, 3000)
  })
}

function startPolling () {
  pollingActive.value = true
  pollingInterval = setInterval(async () => {
    try {
      const { data } = await getAppointmentPaymentStatus(appointment.value.id)
      if (data.data?.payment?.status === 'approved') {
        isPaid.value = true
        stopPolling()
      }
    } catch {}
  }, 3000)
}

function stopPolling () {
  pollingActive.value = false
  if (pollingInterval) clearInterval(pollingInterval)
}

onMounted(() => loadAppointment())
onUnmounted(() => stopPolling())
</script>
